// 继承路由生效配置接口；对象版本用于条件保存（D129、docs/04）。
import { api } from "./api";
import { resource, type EntityStatus } from "./master";

/** 规则自身的匹配条件：只有 MCC/MNC/发送方，没有时间条件（D132）；优先级属于绑定。 */
export interface RouteConditions {
  mcc: string; mnc: string; senders: string[] | null;
}
/** 可共享的独立规则；目标为通道或通道组合二选一，版本用于条件保存（D131）。 */
export interface RoutingRule {
  id: number; name: string; version: number; match: RouteConditions;
  channel_id?: number; channel_group_id?: number;
}
/** 新建时版本由服务器生成，更新时必须保留打开表单时的版本。 */
export type RoutingRuleInput = Omit<RoutingRule, "id">;
/** 持续继承的模板，成员只能是规则，不能嵌套模板。 */
export interface RoutingTemplate {
  id: number; name: string; version: number; status: EntityStatus; note: string | null;
  items: { id: number; rule_id: number; priority: number }[];
}
/** 整体保存模板成员，新绑定由服务端生成身份。 */
export type RoutingTemplateInput = Omit<RoutingTemplate, "id">;
/** 账号绑定的目标二选一：规则或模板，按 target_type 只携带对应 ID（D131）。 */
export type AccountTargetType = "rule" | "template";
/** 账号绑定具有独立优先级与启用状态，没有有效期（D132）。 */
export interface AccountRouteBinding { id: number; target_type: AccountTargetType; rule_id?: number; template_id?: number; priority: number; enabled: boolean }
/** 账号路由对象的版本独立于账号元数据版本。 */
export interface AccountRouting { account_id: number; version: number; bindings: AccountRouteBinding[] }
/** 整组保存，每条绑定只引用已有规则或模板（D131、D150）。 */
export interface AccountRoutingInput { version: number; bindings: AccountRouteBinding[] }
/** 引用范围含停用、过期及经模板间接引用的账号；模板只被账号直接绑定引用（D131）。 */
export interface RoutingReferences { account_ids: number[]; template_ids: number[] }
/** 生效路由模式与最新发布版本。 */
export interface RoutingState { mode: "active"; config_version: number }

export const ROUTING_INVALIDATE = ["routing-state", "routing-rules", "routing-templates", "account-routing", "accounts", "config-versions", "audit"];

/** 独立规则读写与未引用删除，保留 version并发条件。 */
export const routingRules = {
  ...resource<RoutingRule, RoutingRuleInput>("/routing-rules"),
  references: (id: number) => api<RoutingReferences>(`/routing-rules/${id}/references`),
  remove: (row: RoutingRule) => api<void>(`/routing-rules/${row.id}`, { method: "DELETE", body: JSON.stringify({ version: row.version }) }),
};
/** 模板整体读写与未引用删除，账号持续继承其有效快照。 */
export const routingTemplates = {
  ...resource<RoutingTemplate, RoutingTemplateInput>("/routing-templates"),
  references: (id: number) => api<RoutingReferences>(`/routing-templates/${id}/references`),
  remove: (row: RoutingTemplate) => api<void>(`/routing-templates/${row.id}`, { method: "DELETE", body: JSON.stringify({ version: row.version }) }),
};
/** 账号绑定整体保存，只引用已有规则或模板（D131、D150）。 */
export const accountRouting = {
  get: (id: number) => api<AccountRouting>(`/accounts/${id}/routing`),
  update: (id: number, body: AccountRoutingInput) => api<AccountRouting>(`/accounts/${id}/routing`, { method: "PUT", body: JSON.stringify(body) }),
};
/** 读取生效路由的发布状态。 */
export const routingMaintenance = {
  state: () => api<RoutingState>("/routing/state"),
};
